import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Calculator, 
  Zap, 
  Gauge, 
  Layers, 
  Wind, 
  AlertTriangle,
  Lightbulb,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Save,
  RefreshCw,
  Clock,
  Flame,
  Target,
  FlaskConical,
  Grid3X3,
  Users
} from 'lucide-react';
import { LaserTestGridModal, TimeEstimator } from '@/components/CalibrationTools';

const LaserCalculator = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lasers, setLasers] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Form state
  const [selectedLaser, setSelectedLaser] = useState('');
  const [laserType, setLaserType] = useState('co2');
  const [laserPower, setLaserPower] = useState(40); // Watts
  const [material, setMaterial] = useState('');
  const [thickness, setThickness] = useState(3);
  const [mode, setMode] = useState('cut'); // cut or engrave
  const [powerPercent, setPowerPercent] = useState(80);
  const [speed, setSpeed] = useState(10);
  const [passes, setPasses] = useState(1);
  const [airAssist, setAirAssist] = useState(true);
  const [focusHeight, setFocusHeight] = useState(2.0);
  const [lineInterval, setLineInterval] = useState(0.1); // For engraving

  // Results state
  const [results, setResults] = useState(null);
  const [showTestGridModal, setShowTestGridModal] = useState(false);
  const [resumeData, setResumeData] = useState(() => {
    try {
      const raw = localStorage.getItem('laser:resume_calc');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // Costing state
  const [currency, setCurrency] = useState(() => localStorage.getItem('cnc:currency') || 'ZAR');
  const [costInputs, setCostInputs] = useState({
    machineRate: '', jobHours: '', jobMinutes: '',
    elecWatts: '', elecRate: '', materialCost: '',
    wearRate: '', markupPercent: '',
    gasAirCost: '', lensCost: '', wastePercent: ''
  });

  const currencySymbols = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const sym = currencySymbols[currency] || currency;

  const handleCurrencyChange = (val) => {
    setCurrency(val);
    localStorage.setItem('cnc:currency', val);
  };

  const setCost = (field, val) => setCostInputs(prev => ({ ...prev, [field]: val }));

  const costCalc = useMemo(() => {
    const totalMinutes = (parseFloat(costInputs.jobHours) || 0) * 60 + (parseFloat(costInputs.jobMinutes) || 0);
    if (totalMinutes <= 0) return null;
    const totalHours = totalMinutes / 60;
    const machineCost = (parseFloat(costInputs.machineRate) || 0) * totalHours;
    const elecCost = ((parseFloat(costInputs.elecWatts) || 0) / 1000) * totalHours * (parseFloat(costInputs.elecRate) || 0);
    const baseMaterial = parseFloat(costInputs.materialCost) || 0;
    const waste = parseFloat(costInputs.wastePercent) || 0;
    const materialCost = baseMaterial * (1 + waste / 100) + (parseFloat(costInputs.gasAirCost) || 0) + (parseFloat(costInputs.lensCost) || 0);
    const wearCost = machineCost * ((parseFloat(costInputs.wearRate) || 0) / 100);
    const subtotal = machineCost + elecCost + materialCost + wearCost;
    const markupAmount = subtotal * ((parseFloat(costInputs.markupPercent) || 0) / 100);
    const total = subtotal + markupAmount;
    const perMinute = total / totalMinutes;
    return { machineCost, elecCost, materialCost, wearCost, subtotal, markupAmount, total, perMinute };
  }, [costInputs]);

  const materials = [
    { value: 'plywood', label: 'Plywood', maxThickness: 12 },
    { value: 'mdf', label: 'MDF', maxThickness: 12 },
    { value: 'hardwood', label: 'Hardwood', maxThickness: 10 },
    { value: 'softwood', label: 'Softwood', maxThickness: 15 },
    { value: 'acrylic', label: 'Acrylic', maxThickness: 10 },
    { value: 'leather', label: 'Leather', maxThickness: 3 },
    { value: 'fabric', label: 'Fabric', maxThickness: 2 },
    { value: 'paper', label: 'Paper/Cardboard', maxThickness: 3 },
    { value: 'cork', label: 'Cork', maxThickness: 6 },
    { value: 'rubber', label: 'Rubber', maxThickness: 3 },
    { value: 'anodized_aluminum', label: 'Anodized Aluminum', maxThickness: 0, engraveOnly: true },
    { value: 'painted_metal', label: 'Painted Metal', maxThickness: 0, engraveOnly: true },
    { value: 'tile', label: 'Ceramic Tile', maxThickness: 0, engraveOnly: true },
    { value: 'glass', label: 'Glass', maxThickness: 0, engraveOnly: true },
    { value: 'slate', label: 'Slate', maxThickness: 0, engraveOnly: true },
  ];

  // Fetch user's laser machines
  useEffect(() => {
    const fetchLasers = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/laser/machines`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLasers(response.data || []);
      } catch (error) {
        console.error('Failed to fetch lasers:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLasers();
  }, [token]);

  // Calculate recommended settings
  const calculateSettings = () => {
    if (!material) {
      toast.error('Please select a material');
      return;
    }

    const materialData = materials.find(m => m.value === material);
    
    // Base calculations (these would be more sophisticated in production)
    let recPower, recSpeed, recPasses, warnings = [], tips = [];
    
    if (mode === 'cut') {
      // Cutting calculations based on material and thickness
      const basePower = laserType === 'co2' ? 70 : 90;
      const baseSpeed = laserType === 'co2' ? 15 : 5;
      
      // Adjust for thickness
      const thicknessMultiplier = 1 + (thickness - 3) * 0.15;
      recPower = Math.min(100, Math.round(basePower * thicknessMultiplier));
      recSpeed = Math.max(1, Math.round(baseSpeed / thicknessMultiplier));
      recPasses = thickness > 6 ? 2 : 1;
      
      // Material-specific adjustments
      if (material === 'acrylic') {
        recSpeed = Math.round(recSpeed * 0.8);
        tips.push('Use low air assist to prevent frosted edges on acrylic');
      } else if (material === 'leather') {
        recPower = Math.round(recPower * 0.6);
        tips.push('Leather cuts easily - start with lower power to avoid burning');
      } else if (material === 'plywood' || material === 'mdf') {
        tips.push('Check for glue layers that may require extra passes');
      }
      
      // Warnings
      if (thickness > 6 && laserType === 'diode') {
        warnings.push('Diode lasers struggle with thick materials - consider multiple passes');
      }
      if (!airAssist) {
        warnings.push('Air assist recommended for cutting to prevent flare-ups');
      }
      
    } else {
      // Engraving calculations
      recPower = laserType === 'co2' ? 15 : 30;
      recSpeed = laserType === 'co2' ? 300 : 1000;
      recPasses = 1;
      
      // Material-specific engraving
      if (material === 'anodized_aluminum' || material === 'painted_metal') {
        recPower = laserType === 'diode' ? 50 : 20;
        recSpeed = 200;
        tips.push('Diode lasers excel at marking anodized and painted metals');
      } else if (material === 'glass') {
        recPower = 25;
        recSpeed = 150;
        tips.push('Apply wet newspaper or masking to help glass engraving');
      } else if (material === 'leather') {
        recPower = 12;
        recSpeed = 200;
        tips.push('Test on scrap - leather darkens quickly');
      }
    }

    // Kerf width estimation (approximate)
    const kerfWidth = laserType === 'co2' ? 0.2 : 0.1;
    
    // Time estimation (very rough)
    const estimatedTime = mode === 'cut' 
      ? `${Math.round(100 / recSpeed * recPasses)} min per 100mm`
      : 'Depends on design complexity';

    setResults({
      power: recPower,
      speed: recSpeed,
      passes: recPasses,
      kerfWidth,
      estimatedTime,
      warnings,
      tips,
      mode,
      material: materialData?.label,
      laserType
    });

    // Save for Resume Last Job
    try {
      const snap = { material, thickness, mode, laserType, laserPower, airAssist, savedAt: Date.now() };
      localStorage.setItem('laser:resume_calc', JSON.stringify(snap));
      setResumeData(snap);
    } catch {}

    // Update form with calculated values
    setPowerPercent(recPower);
    setSpeed(recSpeed);
    setPasses(recPasses);
  };

  // Save calculation
  const saveCalculation = async () => {
    if (!results) {
      toast.error('Calculate settings first');
      return;
    }

    try {
      await axios.post(`${API}/laser/history`, {
        material,
        thickness,
        mode,
        power: powerPercent,
        speed,
        passes,
        air_assist: airAssist,
        laser_type: laserType,
        laser_power: laserPower
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Calculation saved!');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save calculation');
    }
  };

  const selectedMaterial = materials.find(m => m.value === material);
  const isEngraveOnly = selectedMaterial?.engraveOnly;

  if (loading) {
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center"
        style={bgStyle(IMAGES.LASER_CALCULATOR)}
      >
        <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <Calculator className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading calculator...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
      data-testid="laser-calculator"
      style={bgStyle(IMAGES.LASER_CALCULATOR)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <Calculator className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">LASER SETTINGS</div>
            <h1 
              className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Laser Calculator
            </h1>
            <p className="text-sm md:text-base text-zinc-300 mt-1">Get optimal cutting and engraving settings</p>
          </div>
        </div>
      </div>

      {/* Resume Last Job */}
      {resumeData?.material && (
        <div className="flex items-center justify-between p-3 mb-4 rounded-sm border border-orange-500/40 bg-orange-500/10">
          <div className="text-sm text-orange-400">
            Resume last job: <span className="font-semibold">{resumeData.material} · {resumeData.mode}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setMaterial(resumeData.material);
              if (resumeData.thickness) setThickness(resumeData.thickness);
              setMode(resumeData.mode);
              setLaserType(resumeData.laserType);
              if (resumeData.laserPower) setLaserPower(resumeData.laserPower);
              if (resumeData.airAssist !== undefined) setAirAssist(resumeData.airAssist);
              setResumeData(null);
              localStorage.removeItem('laser:resume_calc');
              toast.success('Last job restored');
            }}
            className="text-xs px-3 py-1 rounded border border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-black transition-colors"
          >
            Restore
          </button>
        </div>
      )}

      {/* Quick-Start Presets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Cut 3mm Acrylic', sub: 'Acrylic · 3mm · cut · CO2', apply: () => { setMaterial('acrylic'); setThickness(3); setMode('cut'); setLaserType('co2'); } },
          { label: 'Engrave Timber', sub: 'Plywood · engrave · CO2', apply: () => { setMaterial('plywood'); setMode('engrave'); setLaserType('co2'); } },
          { label: 'Mark Anodised Aluminium', sub: 'Anod. aluminium · engrave', apply: () => { setMaterial('anodized_aluminum'); setMode('engrave'); setLaserType('co2'); } },
          { label: 'Cut 1mm Leather', sub: 'Leather · 1mm · cut · CO2', apply: () => { setMaterial('leather'); setThickness(1); setMode('cut'); setLaserType('co2'); } },
        ].map(({ label, sub, apply }) => (
          <button
            key={label}
            type="button"
            onClick={() => { apply(); toast.success('Preset loaded — adjust as needed'); }}
            className="text-left p-3 rounded-sm border border-zinc-700 bg-black/50 hover:border-orange-500 hover:bg-orange-500/10 transition-colors group"
          >
            <div className="text-sm font-semibold text-white group-hover:text-orange-400">{label}</div>
            <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Section */}
        <div className="lg:col-span-2 space-y-4">
          {/* Laser Selection */}
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-500" />
                Laser Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Laser Type</Label>
                  <Select value={laserType} onValueChange={setLaserType}>
                    <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="co2">CO2 Laser</SelectItem>
                      <SelectItem value="diode">Diode Laser</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Laser Power (Watts)</Label>
                  <Select value={laserPower.toString()} onValueChange={(v) => setLaserPower(parseInt(v))}>
                    <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {laserType === 'co2' ? (
                        <>
                          <SelectItem value="40">40W</SelectItem>
                          <SelectItem value="50">50W</SelectItem>
                          <SelectItem value="60">60W</SelectItem>
                          <SelectItem value="80">80W</SelectItem>
                          <SelectItem value="100">100W</SelectItem>
                          <SelectItem value="130">130W</SelectItem>
                          <SelectItem value="150">150W</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="5">5W</SelectItem>
                          <SelectItem value="10">10W</SelectItem>
                          <SelectItem value="20">20W</SelectItem>
                          <SelectItem value="30">30W</SelectItem>
                          <SelectItem value="40">40W</SelectItem>
                          <SelectItem value="60">60W</SelectItem>
                          <SelectItem value="80">80W</SelectItem>
                          <SelectItem value="100">100W</SelectItem>
                          <SelectItem value="120">120W</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Material Selection */}
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-orange-500" />
                Material
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Material Type</Label>
                  <Select value={material} onValueChange={setMaterial}>
                    <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 max-h-[300px]">
                      {materials.map(m => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label} {m.engraveOnly && '(Engrave only)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Thickness (mm)</Label>
                  <Input
                    type="number"
                    value={thickness}
                    onChange={(e) => setThickness(parseFloat(e.target.value) || 0)}
                    min={0}
                    max={20}
                    step={0.5}
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                    disabled={isEngraveOnly}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Operation Mode */}
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-orange-500" />
                Operation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Mode</Label>
                <div className="flex gap-2">
                  <Button
                    variant={mode === 'cut' ? 'default' : 'outline'}
                    className={mode === 'cut' 
                      ? 'bg-red-600 hover:bg-red-500' 
                      : 'border-zinc-700 hover:border-zinc-600'}
                    onClick={() => setMode('cut')}
                    disabled={isEngraveOnly}
                  >
                    <Flame className="w-4 h-4 mr-2" />
                    Cut
                  </Button>
                  <Button
                    variant={mode === 'engrave' ? 'default' : 'outline'}
                    className={mode === 'engrave' 
                      ? 'bg-blue-600 hover:bg-blue-500' 
                      : 'border-zinc-700 hover:border-zinc-600'}
                    onClick={() => setMode('engrave')}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Engrave
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-zinc-300">Air Assist</Label>
                  <p className="text-xs text-zinc-500">Recommended for cutting</p>
                </div>
                <Switch
                  checked={airAssist}
                  onCheckedChange={setAirAssist}
                />
              </div>
            </CardContent>
          </Card>

          {/* Advanced Settings */}
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader 
              className="pb-3 cursor-pointer"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Gauge className="w-5 h-5 text-orange-500" />
                  Manual Override
                </CardTitle>
                {showAdvanced ? (
                  <ChevronUp className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                )}
              </div>
            </CardHeader>
            {showAdvanced && (
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-zinc-300">Power: {powerPercent}%</Label>
                  </div>
                  <Slider
                    value={[powerPercent]}
                    onValueChange={([v]) => setPowerPercent(v)}
                    min={1}
                    max={100}
                    step={1}
                    className="py-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Speed (mm/s)</Label>
                  <Input
                    type="number"
                    value={speed}
                    onChange={(e) => setSpeed(parseFloat(e.target.value) || 1)}
                    min={1}
                    max={1000}
                    className="bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Passes</Label>
                  <Select value={passes.toString()} onValueChange={(v) => setPasses(parseInt(v))}>
                    <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? 'Pass' : 'Passes'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {mode === 'engrave' && (
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Line Interval (mm)</Label>
                    <Input
                      type="number"
                      value={lineInterval}
                      onChange={(e) => setLineInterval(parseFloat(e.target.value) || 0.1)}
                      min={0.01}
                      max={1}
                      step={0.01}
                      className="bg-zinc-800/50 border-zinc-700 text-white"
                    />
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Calculate Button */}
          <Button 
            onClick={calculateSettings}
            className="w-full bg-orange-600 hover:bg-orange-500 text-white py-6 text-lg"
          >
            <Calculator className="w-5 h-5 mr-2" />
            Calculate Settings
          </Button>
        </div>

        {/* Results Section */}
        <div className="space-y-4">
          {results ? (
            <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 sticky top-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Recommended Settings</CardTitle>
                  <Badge className={results.mode === 'cut' ? 'bg-red-600' : 'bg-blue-600'}>
                    {results.mode}
                  </Badge>
                </div>
                <CardDescription className="text-zinc-400">
                  {results.material} • {results.laserType.toUpperCase()}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Main Settings */}
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-zinc-500 uppercase">Power</div>
                      <div className="text-2xl font-bold text-orange-400">{results.power}%</div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 uppercase">Speed</div>
                      <div className="text-2xl font-bold text-blue-400">{results.speed}mm/s</div>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="p-4 bg-zinc-800/50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Passes</span>
                    <span className="text-white font-medium">{results.passes}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Kerf Width</span>
                    <span className="text-white font-medium">~{results.kerfWidth}mm</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Air Assist</span>
                    <span className="text-white font-medium">{airAssist ? 'On' : 'Off'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Est. Time</span>
                    <span className="text-white font-medium">{results.estimatedTime}</span>
                  </div>
                </div>

                {/* Warnings */}
                {results.warnings.length > 0 && (
                  <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-medium text-red-400">Warnings</span>
                    </div>
                    <ul className="space-y-1">
                      {results.warnings.map((warning, idx) => (
                        <li key={idx} className="text-sm text-zinc-300">• {warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tips */}
                {results.tips.length > 0 && (
                  <div className="p-4 bg-blue-500/20 border border-blue-500/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-medium text-blue-400">Tips</span>
                    </div>
                    <ul className="space-y-1">
                      {results.tips.map((tip, idx) => (
                        <li key={idx} className="text-sm text-zinc-300">• {tip}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    onClick={saveCalculation}
                    className="flex-1 bg-green-600 hover:bg-green-500"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button
                    onClick={() => setResults(null)}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>

                {/* Test Grid Button */}
                <div className="pt-4 border-t border-zinc-700">
                  <Button
                    onClick={() => setShowTestGridModal(true)}
                    variant="outline"
                    className="w-full border-orange-500 text-orange-500 hover:bg-orange-500/10"
                  >
                    <Grid3X3 className="w-5 h-5 mr-2" />
                    Generate Test Grid
                  </Button>
                  <p className="text-xs text-zinc-500 text-center mt-2">
                    Find optimal power/speed for this material
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 h-full flex items-center justify-center">
              <CardContent className="text-center py-12">
                <Calculator className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-zinc-400 mb-2">
                  Configure Your Settings
                </h3>
                <p className="text-sm text-zinc-500 max-w-xs mx-auto">
                  Select your laser type, material, and operation mode, then click Calculate to get recommended settings.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ===================== */}
      {/* JOB COSTING CALCULATOR */}
      {/* ===================== */}
      <div className="mt-6">
        <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-orange-500" />
              Job Costing Calculator
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Estimate total job cost — updates live
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Currency */}
            <div>
              <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Currency</div>
              <div className="grid grid-cols-4 gap-2">
                {['ZAR','USD','EUR','GBP'].map(c => (
                  <button key={c} onClick={() => handleCurrencyChange(c)}
                    className={`py-2 rounded-lg border text-sm font-bold transition-all
                      ${currency === c
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'border-zinc-700 text-zinc-400 hover:border-zinc-500 bg-zinc-800/50'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div>
              <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Job Duration</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Hours</label>
                  <input type="number" min="0" value={costInputs.jobHours}
                    onChange={e => setCost('jobHours', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Minutes</label>
                  <input type="number" min="0" max="59" value={costInputs.jobMinutes}
                    onChange={e => setCost('jobMinutes', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            {/* Operating */}
            <div>
              <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Operating Costs</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Machine Rate ({sym}/hr)</label>
                  <input type="number" min="0" value={costInputs.machineRate}
                    onChange={e => setCost('machineRate', e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Electricity (Watts)</label>
                  <input type="number" min="0" value={costInputs.elecWatts}
                    onChange={e => setCost('elecWatts', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Electricity Rate ({sym}/kWh)</label>
                  <input type="number" min="0" value={costInputs.elecRate}
                    onChange={e => setCost('elecRate', e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Material Cost ({sym})</label>
                  <input type="number" min="0" value={costInputs.materialCost}
                    onChange={e => setCost('materialCost', e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            {/* Laser-specific */}
            <div>
              <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Laser Consumables</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Gas/Air Cost ({sym})</label>
                  <input type="number" min="0" value={costInputs.gasAirCost}
                    onChange={e => setCost('gasAirCost', e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Lens Cleaning ({sym})</label>
                  <input type="number" min="0" value={costInputs.lensCost}
                    onChange={e => setCost('lensCost', e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Material Waste (%)</label>
                  <input type="number" min="0" max="100" value={costInputs.wastePercent}
                    onChange={e => setCost('wastePercent', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            {/* Overheads */}
            <div>
              <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Overheads & Markup</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Wear & Tear (%)</label>
                  <input type="number" min="0" max="100" value={costInputs.wearRate}
                    onChange={e => setCost('wearRate', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Markup (%)</label>
                  <input type="number" min="0" value={costInputs.markupPercent}
                    onChange={e => setCost('markupPercent', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            {/* Results */}
            {costCalc ? (
              <div className="p-4 bg-zinc-800/60 border border-zinc-600 rounded-lg space-y-2">
                <div className="flex justify-between text-sm text-zinc-300">
                  <span>Machine cost</span>
                  <span>{sym}{costCalc.machineCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-300">
                  <span>Electricity</span>
                  <span>{sym}{costCalc.elecCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-300">
                  <span>Material &amp; consumables</span>
                  <span>{sym}{costCalc.materialCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-300">
                  <span>Wear &amp; tear</span>
                  <span>{sym}{costCalc.wearCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-400 border-t border-zinc-600 pt-2">
                  <span>Subtotal</span>
                  <span>{sym}{costCalc.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-400">
                  <span>Markup</span>
                  <span>+{sym}{costCalc.markupAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-orange-400 border-t border-zinc-600 pt-3">
                  <span>TOTAL JOB COST</span>
                  <span>{sym}{costCalc.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-zinc-500">
                  <span>Cost per minute</span>
                  <span>{sym}{costCalc.perMinute.toFixed(2)}/min</span>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-zinc-800/30 border border-zinc-700 rounded-lg text-center">
                <p className="text-zinc-500 text-sm">Enter job duration to see cost breakdown</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Test Grid Modal */}
      <LaserTestGridModal
        material={material || 'plywood'}
        laserType={laserType}
        laserPower={laserPower}
        isOpen={showTestGridModal}
        onClose={() => setShowTestGridModal(false)}
      />
    </div>
  );
};

export default LaserCalculator;
